import React, { useEffect, useState, useRef } from 'react';
import CountUp from 'react-countup';
import { RefreshCwIcon } from 'lucide-react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { statsData } from '../utils/constants';
const Stats = ({
  language = 'en'
}) => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [currencyMode, setCurrencyMode] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [exitDirection, setExitDirection] = useState<'left' | 'right' | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);
  const toggleCurrency = statId => {
    setCurrencyMode(prev => ({
      ...prev,
      [statId]: !prev[statId]
    }));
  };
  const translations = {
    en: {
      myNumbers: 'My Numbers',
      scholarship: 'Northeastern: Scholarship',
      toggleCurrency: 'Toggle currency',
      achievements: 'Achievements',
      swipeHint: '← Swipe to browse →'
    },
    vi: {
      myNumbers: 'Thành Tích Của Tôi',
      scholarship: 'Học bổng Northeastern',
      toggleCurrency: 'Chuyển đổi tiền tệ',
      achievements: 'Thành tựu',
      swipeHint: '← Vuốt để xem →'
    }
  };
  const t = translations[language];
  const northeasternScholarship = {
    id: 'northeastern-scholarship',
    label: t.scholarship,
    value: 364,
    suffix: 'K USD',
    alternateValue: 9.6,
    alternateSuffix: 'B VND',
    hasCurrencyToggle: true,
    duration: 5.5
  };
  const allStats = [northeasternScholarship, ...statsData[0].stats.filter(stat => stat.id !== 'gpa')];
  const handleDragEnd = (event: any, info: PanInfo) => {
    const swipeThreshold = 100;
    const swipeVelocityThreshold = 500;
    if (Math.abs(info.offset.x) > swipeThreshold || Math.abs(info.velocity.x) > swipeVelocityThreshold) {
      if (info.offset.x > 0) {
        setExitDirection('right');
      } else {
        setExitDirection('left');
      }
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % allStats.length);
        setExitDirection(null);
      }, 300);
    }
  };
  const currentStat = allStats[currentIndex];
  const nextStat = allStats[(currentIndex + 1) % allStats.length];
  return <section ref={containerRef} className="w-full py-16 bg-background" data-id="element-1053">
      <div className="container mx-auto px-4" data-id="element-1054">
        <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12 fade-in" data-id="element-1055">
          {t.myNumbers} <span className="gradient-text" data-id="element-1056">{t.achievements}</span>
        </h2>

        {/* Mobile Swipe View */}
        {isMobile && <div className="md:hidden relative px-2 mb-8" data-id="element-1057">
            <div className="relative h-[280px] flex items-center justify-center" data-id="element-1058">
              <div className="relative w-full max-w-sm h-full" data-id="element-1059">
                <AnimatePresence mode="wait" data-id="element-1060">
                  {nextStat && <motion.div key={`next-${nextStat.id}`} className="absolute inset-0 w-full" initial={{
                scale: 0.9,
                opacity: 0.5
              }} animate={{
                scale: 0.95,
                opacity: 0.7
              }} style={{
                zIndex: 1
              }} data-id="element-1061">
                      <div className="bg-secondary/50 rounded-2xl p-8 text-center h-full flex flex-col justify-center border border-white/5" data-id="element-1062">
                        <h3 className="text-5xl font-display font-bold mb-3 stat-number flex items-center justify-center gap-2" data-id="element-1063">
                          {currencyMode[nextStat.id] && nextStat.hasCurrencyToggle ? nextStat.alternateValue : nextStat.value}
                          <span className="text-3xl" data-id="element-1064">
                            {currencyMode[nextStat.id] && nextStat.hasCurrencyToggle ? nextStat.alternateSuffix : nextStat.suffix}
                          </span>
                        </h3>
                        <p className="text-muted text-base px-4" data-id="element-1065">
                          {language === 'en' ? nextStat.label : nextStat.labelVi || nextStat.label}
                        </p>
                      </div>
                    </motion.div>}

                  {currentStat && !exitDirection && <motion.div key={`current-${currentStat.id}`} className="absolute inset-0 w-full cursor-grab active:cursor-grabbing" drag="x" dragConstraints={{
                left: 0,
                right: 0
              }} dragElastic={0.7} onDragEnd={handleDragEnd} initial={{
                scale: 1,
                opacity: 1,
                rotate: 0
              }} animate={{
                scale: 1,
                opacity: 1,
                rotate: 0
              }} exit={{
                x: exitDirection === 'right' ? 500 : -500,
                opacity: 0,
                rotate: exitDirection === 'right' ? 25 : -25,
                transition: {
                  duration: 0.3,
                  ease: 'easeOut'
                }
              }} whileDrag={{
                scale: 1.05,
                rotate: 0,
                cursor: 'grabbing'
              }} style={{
                zIndex: 2
              }} data-id="element-1066">
                      <div className="bg-gradient-to-br from-secondary to-secondary/80 rounded-2xl p-8 text-center h-full flex flex-col justify-center shadow-2xl border-2 border-white/10 backdrop-blur-sm" data-id="element-1067">
                        <h3 className="text-6xl font-display font-bold mb-4 stat-number flex items-center justify-center gap-3" data-id="element-1068">
                          {isVisible && <>
                              {currencyMode[currentStat.id] && currentStat.hasCurrencyToggle ? <CountUp end={currentStat.alternateValue} duration={currentStat.duration || 6} decimals={currentStat.alternateValue < 100 ? 1 : 0} separator="," useEasing={true} data-id="element-1069" /> : <CountUp end={currentStat.value} duration={currentStat.duration || 6} decimals={currentStat.value % 1 !== 0 ? 1 : 0} separator="," useEasing={true} data-id="element-1070" />}
                            </>}
                          {!isVisible && '0'}
                          <span className="text-3xl" data-id="element-1071">
                            {currencyMode[currentStat.id] && currentStat.hasCurrencyToggle ? currentStat.alternateSuffix : currentStat.suffix}
                          </span>
                          {currentStat.hasCurrencyToggle && <button onClick={() => toggleCurrency(currentStat.id)} className="ml-2 p-2 rounded-full bg-background text-accent-cyan hover:bg-accent-cyan/20 transition-colors" title={t.toggleCurrency} data-id="element-1072">
                              <RefreshCwIcon size={18} className="animate-spin-slow" data-id="element-1073" />
                            </button>}
                        </h3>
                        <p className="text-muted text-lg px-4 leading-relaxed" data-id="element-1074">
                          {language === 'en' ? currentStat.label : currentStat.labelVi || currentStat.label}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>

            <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="text-center mt-6 text-muted text-sm" data-id="element-1075">
              {t.swipeHint}
            </motion.div>

            <div className="flex justify-center gap-2 mt-4 flex-wrap px-4" data-id="element-1076">
              {allStats.map((_, index) => <button key={index} onClick={() => setCurrentIndex(index)} className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-accent-cyan w-6' : 'bg-gray-300 dark:bg-gray-700 w-2'}`} aria-label={`Go to stat ${index + 1}`} data-id="element-1077" />)}
            </div>
          </div>}

        {/* Desktop Grid View */}
        <div className={`${isMobile ? 'hidden' : 'block'} space-y-12`} data-id="element-1078">
          {statsData.map(section => <div key={section.id} className="mb-12" data-id="element-1079">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1080">
                <div className="bg-secondary rounded-2xl p-6 text-center card-glow transform transition-all duration-500 hover:scale-105" data-id="element-1081">
                  <h3 className="text-4xl font-display font-bold mb-2 stat-number flex items-center justify-center gap-2" data-id="element-1082">
                    {isVisible && <>
                        {currencyMode[northeasternScholarship.id] ? <CountUp end={northeasternScholarship.alternateValue} duration={northeasternScholarship.duration || 6} decimals={northeasternScholarship.alternateValue < 100 ? 1 : 0} separator="," useEasing={true} data-id="element-1083" /> : <CountUp end={northeasternScholarship.value} duration={northeasternScholarship.duration || 6} decimals={northeasternScholarship.value % 1 !== 0 ? 1 : 0} separator="," useEasing={true} data-id="element-1084" />}
                      </>}
                    {!isVisible && '0'}
                    {currencyMode[northeasternScholarship.id] ? <span data-id="element-1085">{northeasternScholarship.alternateSuffix}</span> : <span data-id="element-1086">{northeasternScholarship.suffix}</span>}
                    {northeasternScholarship.hasCurrencyToggle && <button onClick={() => toggleCurrency(northeasternScholarship.id)} className="ml-2 p-1 rounded-full bg-background text-accent-cyan hover:bg-accent-cyan/20 transition-colors" title={t.toggleCurrency} data-id="element-1087">
                        <RefreshCwIcon size={16} className="animate-spin-slow" data-id="element-1088" />
                      </button>}
                  </h3>
                  <p className="text-muted text-lg" data-id="element-1089">
                    {northeasternScholarship.label}
                  </p>
                </div>

                {section.stats.filter(stat => stat.id !== 'gpa').map((stat, index) => <div key={stat.id} className="bg-secondary rounded-2xl p-6 text-center card-glow transform transition-all duration-500 hover:scale-105" style={{
              animationDelay: `${index * 0.1}s`
            }} data-id="element-1090">
                      <h3 className="text-4xl font-display font-bold mb-2 stat-number flex items-center justify-center gap-2" data-id="element-1091">
                        {stat.prefix && <span data-id="element-1092">{stat.prefix}</span>}
                        {isVisible && <>
                            {stat.hasCurrencyToggle && currencyMode[stat.id] ? <CountUp end={stat.alternateValue} duration={stat.duration || 6} decimals={stat.alternateValue < 100 ? 1 : 0} separator="," useEasing={true} data-id="element-1093" /> : <CountUp end={stat.value} duration={stat.duration || 6} decimals={stat.value % 1 !== 0 ? 1 : 0} separator="," useEasing={true} data-id="element-1094" />}
                          </>}
                        {!isVisible && '0'}
                        {stat.hasCurrencyToggle && currencyMode[stat.id] ? <span data-id="element-1095">{stat.alternateSuffix}</span> : <span data-id="element-1096">{stat.suffix}</span>}
                        {stat.hasCurrencyToggle && <button onClick={() => toggleCurrency(stat.id)} className="ml-2 p-1 rounded-full bg-background text-accent-cyan hover:bg-accent-cyan/20 transition-colors" title={t.toggleCurrency} data-id="element-1097">
                            <RefreshCwIcon size={16} className="animate-spin-slow" data-id="element-1098" />
                          </button>}
                      </h3>
                      <p className="text-muted text-lg" data-id="element-1099">
                        {language === 'en' ? stat.label : stat.labelVi || stat.label}
                      </p>
                    </div>)}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
export default Stats;